import { type MouseEvent } from 'react';
import { r, s } from '@/theme/tokens';
import { Box, IconButton, Menu, MenuItem, TextField } from '@/ui/primitives';
import { DeleteFilledIcon as DeleteIcon } from '@/ui/primitives/icons';
import type { TagColor } from '@/shared/data/tags';

export const defaultTagColor: TagColor = 'neutral';

const tagColorOptions: Array<{ value: TagColor; swatch: string }> = [
  { value: 'neutral', swatch: '#8D949E' },
  { value: 'green', swatch: '#43A047' },
  { value: 'orange', swatch: '#FB8C00' },
  { value: 'red', swatch: '#EF5350' },
  { value: 'blue', swatch: '#42A5F5' },
  { value: 'purple', swatch: '#AB47BC' },
  { value: 'teal', swatch: '#00ACC1' },
  { value: 'brown', swatch: '#8D6E63' },
];

const tagColorSwatchSize = '18px';
const tagColorSwatchRadius = '6px';
const tagColorControlSize = '32px';
const tagColorSelectWidth = tagColorControlSize;
const tagColorSwatchSx = {
  display: 'inline-flex',
  width: tagColorSwatchSize,
  height: tagColorSwatchSize,
  minWidth: tagColorSwatchSize,
  minHeight: tagColorSwatchSize,
  maxWidth: tagColorSwatchSize,
  maxHeight: tagColorSwatchSize,
  flex: '0 0 auto',
  flexShrink: 0,
  aspectRatio: '1 / 1',
  boxSizing: 'border-box',
  borderRadius: tagColorSwatchRadius,
  border: '1px solid',
  borderColor: 'divider',
} as const;

export type TableRowTagColorMenuState = {
  index: number;
  anchorEl: HTMLElement;
} | null;

type TableRowTagEditorRowProps = {
  colorMenuState: TableRowTagColorMenuState;
  index: number;
  onCloseColorMenu: () => void;
  onColorChange: (index: number, color: TagColor) => void;
  onOpenColorMenu: (index: number, anchorEl: HTMLElement) => void;
  onRemove: (index: number) => void;
  onTagChange: (index: number, value: string) => void;
  selectedColor: TagColor;
  tag: string;
};

export const TableRowTagEditorRow = ({
  colorMenuState,
  index,
  onCloseColorMenu,
  onColorChange,
  onOpenColorMenu,
  onRemove,
  onTagChange,
  selectedColor,
  tag,
}: TableRowTagEditorRowProps): JSX.Element => {
  const selectedSwatch =
    tagColorOptions.find((option) => option.value === selectedColor)?.swatch ??
    tagColorOptions[0].swatch;

  const handleOpenColorMenu = (event: MouseEvent<HTMLElement>): void => {
    event.stopPropagation();
    onOpenColorMenu(index, event.currentTarget);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: s('sm'), width: '100%' }}>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'grid',
          gridTemplateColumns: `minmax(0, 1fr) ${tagColorSelectWidth}`,
          alignItems: 'center',
          gap: s('md'),
        }}
      >
        <TextField
          size="small"
          inputHeight={32}
          value={tag}
          onChange={(event) => {
            onTagChange(index, event.target.value);
          }}
          fullWidth
          sx={(theme) => ({
            '& .MuiInputBase-root, & .MuiOutlinedInput-root': {
              backgroundColor: 'background.default',
              minHeight: tagColorControlSize,
              height: tagColorControlSize,
            },
            '& .MuiInputBase-input, & .MuiOutlinedInput-input': {
              fontSize: theme.typography.subtitle1.fontSize,
              lineHeight: theme.typography.subtitle1.lineHeight,
            },
          })}
        />
        <Box
          role="button"
          tabIndex={0}
          aria-label={`Choose color for tag ${index + 1}`}
          onClick={handleOpenColorMenu}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            event.stopPropagation();
            onOpenColorMenu(index, event.currentTarget as HTMLElement);
          }}
          sx={{
            minWidth: tagColorSelectWidth,
            width: tagColorSelectWidth,
            maxWidth: tagColorSelectWidth,
            minHeight: tagColorControlSize,
            height: tagColorControlSize,
            maxHeight: tagColorControlSize,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: r('md'),
            backgroundColor: 'background.default',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <Box component="span" sx={[tagColorSwatchSx, { backgroundColor: selectedSwatch }]} />
        </Box>
      </Box>
      <Menu
        anchorEl={colorMenuState?.anchorEl ?? null}
        open={colorMenuState?.index === index}
        onClose={onCloseColorMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              minWidth: tagColorSelectWidth,
              width: tagColorSelectWidth,
              maxWidth: tagColorSelectWidth,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: `${r('md')} !important`,
              backgroundColor: 'background.default',
              backgroundImage: 'none',
              boxShadow: 'none',
              overflow: 'hidden',
            },
          },
          list: {
            sx: {
              p: s('none'),
              m: s('none'),
              backgroundColor: 'background.default',
            },
          },
        }}
      >
        {tagColorOptions.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => {
              onColorChange(index, option.value);
              onCloseColorMenu();
            }}
            sx={{
              minHeight: `calc(${s('2xl')} + ${s('xs')})`,
              px: s('md'),
              py: s('md'),
              justifyContent: 'center',
              borderRadius: r('none'),
              backgroundColor: selectedColor === option.value ? 'action.selected' : undefined,
            }}
          >
            <Box component="span" sx={[tagColorSwatchSx, { backgroundColor: option.swatch }]} />
          </MenuItem>
        ))}
      </Menu>
      <IconButton
        aria-label={`Remove tag ${index + 1}`}
        size="medium"
        sx={{
          width: `calc(${s('2xl')} + ${s('sm')})`,
          height: `calc(${s('2xl')} + ${s('sm')})`,
          minWidth: `calc(${s('2xl')} + ${s('sm')})`,
          minHeight: `calc(${s('2xl')} + ${s('sm')})`,
          p: s('none'),
          borderRadius: r('pill'),
          '&:hover, &.Mui-focusVisible': {
            backgroundColor: 'action.hover',
          },
        }}
        onClick={() => {
          onRemove(index);
        }}
      >
        <DeleteIcon sx={{ color: 'error.main', fontSize: `calc(${s('2xl')} + ${s('xs')})` }} />
      </IconButton>
    </Box>
  );
};
